import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import Seo, { breadcrumbJsonLd } from "@/components/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/posts/${slug}`);
        setPost(data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <div className="font-display text-4xl font-black mb-4">Post not found</div>
        <button onClick={() => navigate("/insights")} className="btn-secondary mt-4">Back to Insights</button>
      </div>
    );
  }

  const articleBody = (post.content || []).map((b) => b.text).join("\n\n");

  // If the post reads like a playbook (has h2 "steps"/"rules"/"week" etc.),
  // we also emit HowTo JSON-LD alongside BlogPosting — Google shows HowTo
  // cards with step snippets in SERPs.
  const h2s = (post.content || []).filter((b) => b.type === "h2");
  const isHowTo = h2s.length >= 2 && /\d+|step|week|rule|phase/i.test(h2s.map((h) => h.text).join(" "));
  const howToSteps = [];
  if (isHowTo) {
    // Pair each h2 with the paragraph immediately after it.
    for (let i = 0; i < post.content.length; i++) {
      if (post.content[i].type === "h2") {
        const next = post.content[i + 1];
        howToSteps.push({
          "@type": "HowToStep",
          name: post.content[i].text,
          text: next && next.text ? next.text : post.content[i].text,
        });
      }
    }
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: post.title,
      description: post.excerpt,
      image: post.cover_img,
      url: `https://spinlabs.in/insights/${post.slug}`,
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at || post.published_at || post.created_at,
      articleSection: post.category,
      articleBody,
      wordCount: articleBody ? articleBody.split(/\s+/).filter(Boolean).length : undefined,
      author: { "@type": "Person", name: post.author, url: "https://spinlabs.in/about" },
      publisher: {
        "@type": "Organization",
        name: "Spin Labs",
        logo: { "@type": "ImageObject", url: "https://spinlabs.in/favicon-512.png" },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://spinlabs.in/insights/${post.slug}` },
      keywords: post.category,
    },
    ...(isHowTo ? [{
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: post.title,
      description: post.excerpt,
      image: post.cover_img,
      step: howToSteps,
    }] : []),
    breadcrumbJsonLd([
      { name: "Home", path: "/" },
      { name: "Insights", path: "/insights" },
      { name: post.title, path: `/insights/${post.slug}` },
    ]),
  ];

  return (
    <article data-testid="blog-post-page">
      <Seo
        title={post.title}
        path={`/insights/${post.slug}`}
        description={post.excerpt}
        image={post.cover_img}
        type="article"
        publishedTime={post.published_at || post.created_at}
        modifiedTime={post.updated_at || post.published_at || post.created_at}
        author={post.author}
        keywords={`${post.category}, ${post.title}, Spin Labs blog`}
        jsonLd={jsonLd}
      />
      <section className="relative pt-32 pb-10 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="spotlight w-[500px] h-[500px] bg-[#00F0FF]/15 top-0 left-1/4" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/insights" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#00F0FF] mb-6">
            <ArrowLeft className="w-4 h-4" /> All insights
          </Link>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="font-mono-neo text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] mb-4">
              {post.category} · {post.read_time}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.035em] leading-[1.02]">
              {post.title}
            </h1>
            <p className="mt-5 text-lg text-white/70 leading-relaxed">{post.excerpt}</p>
            <div className="mt-8 flex items-center gap-3 text-sm text-white/60">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF00FF]" />
              <div>
                <div className="text-white font-medium">{post.author}</div>
                <div className="font-mono-neo text-[10px] uppercase tracking-[0.2em]">Spin Labs</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="relative aspect-[16/10] sm:aspect-[21/9] rounded-2xl overflow-hidden border border-white/10">
            <img src={post.cover_img} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="py-10 lg:py-16">
        <div className="max-w-2xl mx-auto px-6 lg:px-8 space-y-6">
          {(post.content || []).map((block, i) => {
            if (block.type === "h2") {
              return (
                <h2 key={i} className="font-display text-2xl lg:text-3xl font-bold tracking-tight pt-6">
                  {block.text}
                </h2>
              );
            }
            return (
              <p key={i} className="text-lg text-white/80 leading-[1.8]">
                {block.text}
              </p>
            );
          })}
        </div>
      </section>

      <section className="py-20 border-t border-white/5 mt-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter">
            Need the same energy on your product?
          </h2>
          <Link to="/contact" className="mt-8 btn-primary !px-8 !py-4 !text-base group inline-flex">
            Talk to us <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </div>
      </section>
    </article>
  );
}
